import { useLazyQuery } from "@apollo/client";
import { compact, defer, first, last, uniqBy } from "lodash";
import moment from "moment";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  GetMeetingOverviewMeetingsQueryQuery,
  GetMeetingOverviewMeetingsQueryQueryVariables,
  MeetingDialogFragmentFragment,
  MeetingsOverviewMeetingFragment,
} from "types/graphql-schema";

import { currentUserVar } from "@cache/cache";
import { ButtonSize } from "@components/button/button-constants";
import Loading from "@components/loading/loading";
import { Select } from "@components/select/select";
import useUserComboboxQuery from "@components/user-combobox/use-user-combobox-query";
import UserCombobox from "@components/user-combobox/user-combobox";
import { UserComboboxOption } from "@components/user-combobox/user-combobox-list";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull } from "@helpers/helpers";

import getMeetingOverviewMeetingsQuery from "../graphql/get-meeting-overview-meetings-query";
import { MeetingType } from "./meetings-overview-constants";
import OverviewMeetingList from "./overview-meeting-list";

const minimum = 5;

const MeetingOverview = ({
  selectedDay,
  filterClassName,
  meetingListClassName,
  onClickMeetingLink,
  forceTodayScroll,
  compact: isCompact = false,
}: {
  forceTodayScroll: number;
  selectedDay: string;
  filterClassName?: string;
  meetingListClassName?: string;
  onClickMeetingLink?: (meetingId: number, meetingGroupId: number) => void;
  compact?: boolean;
}) => {
  const selectedUser = currentUserVar();
  const todayRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const fetchGenRef = useRef(0);
  const [hasMoreInFuture, setHasMoreInFuture] = useState(false);
  const [hasMoreInPast, setHasMoreInPast] = useState(false);
  const [loadMore, setLoadMore] = useState<"past" | "future" | null>(null);

  const [fillerHeight, setFillerHeight] = useState(0);
  const [meetings, setMeetings] = useState<
    (MeetingsOverviewMeetingFragment | MeetingDialogFragmentFragment)[]
  >([]);
  const [participantFilter, setParticipantFilter] = useState<number | null>(
    null,
  );
  const [meetingTypeFilter, setMeetingTypeFilter] = useState(MeetingType.ALL);

  const initialMeetingDisplayedRange = isCompact
    ? {
        start: moment(selectedDay).startOf("day").format(),
        end: moment(selectedDay).endOf("day").format(),
      }
    : {
        start: moment(selectedDay).startOf("isoWeek").format(),
        end: moment(selectedDay).endOf("isoWeek").format(),
      };
  const [meetingDisplayedRange, setMeetingDisplayedRange] = useState<{
    start: string;
    end: string;
  }>(initialMeetingDisplayedRange);

  const [fetchMeetings, { loading }] = useLazyQuery<
    GetMeetingOverviewMeetingsQueryQuery,
    GetMeetingOverviewMeetingsQueryQueryVariables
  >(getMeetingOverviewMeetingsQuery, {});

  const updateFillerHeight = useCallback(() => {
    if (!todayRef.current || !scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const fillerEl = container.querySelector("#filler");
    const currentFillerHeight = fillerEl ? fillerEl.clientHeight : 0;
    const contentHeight = container.scrollHeight - currentFillerHeight;
    const needed =
      container.clientHeight - (contentHeight - todayRef.current.offsetTop);
    setFillerHeight(Math.max(0, needed));
  }, []);

  const scrollToToday = (instant = false) => {
    if (todayRef.current && scrollContainerRef.current) {
      const scrollTop = todayRef.current.offsetTop;
      scrollContainerRef.current.scrollTo({
        top: scrollTop - 10,
        behavior: instant ? "instant" : "smooth",
      });
    }
  };

  const fetchMoreMeetings = (
    dateRangeStart: string,
    dateRangeEnd: string,
    direction: "future" | "past",
  ) => {
    const gen = fetchGenRef.current;
    return fetchMeetings({
      variables: {
        participants: participantFilter
          ? [participantFilter]
          : [selectedUser.id],
        isOneonone:
          meetingTypeFilter === MeetingType.ALL
            ? undefined
            : meetingTypeFilter === MeetingType.ONE_ON_ONE,
        minimumDateRange: JSON.stringify({
          minimum,
          dateRange: [dateRangeStart, dateRangeEnd],
          direction,
        }),
        orderBy: "start_datetime",
        first: null,
      },
      onCompleted: (response) => {
        if (fetchGenRef.current !== gen) return;
        const newMeetings = response.meetings
          ? assertEdgesNonNull(response.meetings)
          : [];
        setMeetings((prev) => uniqBy([...prev, ...newMeetings], "id"));
        if (loadMore === "future") {
          setHasMoreInFuture(newMeetings.length >= minimum);
        } else if (loadMore === "past") {
          setHasMoreInPast(newMeetings.length >= minimum);
        }

        const firstMeeting = first(newMeetings);
        const lastMeeting = last(newMeetings);

        const sortedDates = compact([
          meetingDisplayedRange.start,
          meetingDisplayedRange.end,
          firstMeeting?.startDatetime &&
            moment(firstMeeting?.startDatetime).startOf("day").format(),
          lastMeeting?.endDatetime &&
            moment(lastMeeting?.endDatetime).endOf("day").format(),
        ]).sort();
        const firstSortedDate = first(sortedDates);
        const lastSortedDate = last(sortedDates);
        if (firstSortedDate && lastSortedDate) {
          setMeetingDisplayedRange({
            start: firstSortedDate,
            end: lastSortedDate,
          });
        }
      },
    });
  };

  useLayoutEffect(() => {
    if (!isCompact) {
      updateFillerHeight();
    }
  }, [meetings, meetingDisplayedRange, isCompact]);

  useEffect(() => {
    if (forceTodayScroll > 0) {
      scrollToToday();
    }
  }, [forceTodayScroll]);

  useEffect(() => {
    if (loadMore === null) {
      return;
    }
    const dateRangeStart =
      loadMore === "past"
        ? moment(meetingDisplayedRange.start)
            .subtract(1, "week")
            .startOf("isoWeek")
            .format()
        : moment(meetingDisplayedRange.end)
            .add(1, "day")
            .startOf("day")
            .format();
    const dateRangeEnd =
      loadMore === "past"
        ? moment(meetingDisplayedRange.start)
            .subtract(1, "day")
            .endOf("day")
            .format()
        : moment(meetingDisplayedRange.end)
            .add(1, "week")
            .endOf("isoWeek")
            .format();
    fetchMoreMeetings(dateRangeStart, dateRangeEnd, loadMore).finally(() => {
      setLoadMore(null);
    });
    // only want to fetch more when index changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMore]);

  const {
    options: assessmentUserFilterOptions,
    query,
    setQuery,
  } = useUserComboboxQuery({});
  const userComboboxValue =
    assessmentUserFilterOptions.find(({ id }) => id === participantFilter) ||
    null;

  const handleChangeParticipantFilter = useCallback(
    (option?: UserComboboxOption) => {
      setParticipantFilter(
        option && typeof option.id === "number" ? option.id : null,
      );
    },
    [],
  );

  const initialFetch = () => {
    const gen = fetchGenRef.current;
    fetchMeetings({
      variables: {
        participants: participantFilter
          ? [participantFilter]
          : [selectedUser.id],
        isOneonone:
          meetingTypeFilter === MeetingType.ALL
            ? undefined
            : meetingTypeFilter === MeetingType.ONE_ON_ONE,
        orderBy: "start_datetime",
        first: null,
        minimumDateRange: JSON.stringify({
          minimum: isCompact ? 0 : minimum,
          dateRange: [
            initialMeetingDisplayedRange.start,
            initialMeetingDisplayedRange.end,
          ],
          direction: "future",
        }),
      },
      onCompleted: (response) => {
        if (fetchGenRef.current !== gen) return;
        const newMeetings = response.meetings
          ? assertEdgesNonNull(response.meetings)
          : [];
        setMeetings((prev) => uniqBy([...prev, ...newMeetings], "id"));
        setHasMoreInPast(true);
        setHasMoreInFuture(newMeetings.length >= minimum);
        if (!isCompact) {
          const firstMeeting = first(newMeetings);
          const lastMeeting = last(newMeetings);

          const sortedDates = compact([
            meetingDisplayedRange.start,
            meetingDisplayedRange.end,
            firstMeeting?.startDatetime &&
              moment(firstMeeting?.startDatetime).startOf("day").format(),
            lastMeeting?.endDatetime &&
              moment(lastMeeting?.endDatetime).endOf("day").format(),
          ]).sort();
          const firstSortedDate = first(sortedDates);
          const lastSortedDate = last(sortedDates);
          if (firstSortedDate && lastSortedDate) {
            setMeetingDisplayedRange({
              start: firstSortedDate,
              end: lastSortedDate,
            });
          }
        }
        if (!isCompact) {
          defer(() => {
            scrollToToday(true);
          });
        }
      },
    });
  };

  useEffect(() => {
    // Increment generation to invalidate any in-flight fetches
    fetchGenRef.current += 1;
    // reset meetings and pagination states
    setMeetings([]);
    setHasMoreInFuture(false);
    setHasMoreInPast(false);
    setMeetingDisplayedRange(initialMeetingDisplayedRange);
    // defer fetch so the state is updated, when function is called.
    defer(() => {
      initialFetch();
    });
  }, [participantFilter, meetingTypeFilter]);

  // When toggling compact mode or changing selected day, re-fetch without
  // clearing existing meetings to avoid a loading flash
  useEffect(() => {
    fetchGenRef.current += 1;
    setMeetingDisplayedRange(initialMeetingDisplayedRange);
    if (isCompact) {
      setFillerHeight(0);
    }
    defer(() => {
      initialFetch();
    });
  }, [isCompact, selectedDay]);

  useEffect(() => {
    initialFetch();
  }, []);

  const meetingsToDisplay = isCompact
    ? meetings.filter((m) =>
        moment(m.startDatetime).isSame(moment(selectedDay), "day"),
      )
    : meetings;

  return (
    <div
      className={classNames(
        "@container/overview-meetings",
        isCompact ? "relative" : "h-full",
      )}
    >
      {!isCompact && (
        <div className={classNames("sticky z-40")}>
          <div
            className={classNames(
              "flex items-center justify-between gap-2 h-14 px-3 border-b",
              filterClassName,
            )}
          >
            <div className="w-1/2">
              <UserCombobox
                onChangeValue={handleChangeParticipantFilter}
                options={assessmentUserFilterOptions}
                value={userComboboxValue}
                placeholder="All people"
                clearable={participantFilter !== null}
                query={query}
                onChangeQuery={setQuery}
                onClearValue={handleChangeParticipantFilter}
                buttonProps={{ size: ButtonSize.medium }}
              />
            </div>
            <div className="w-1/2 flex">
              <div className="flex-1 w-full">
                <Select
                  value={meetingTypeFilter}
                  options={[
                    {
                      value: MeetingType.ALL,
                      label: "All meetings",
                    },
                    {
                      value: MeetingType.ONE_ON_ONE,
                      label: "1-on-1s",
                    },
                    {
                      value: MeetingType.GROUP,
                      label: "Group meetings",
                    },
                  ]}
                  onChange={(opt) => setMeetingTypeFilter(opt.value)}
                  aria-label="Meeting type filter"
                  buttonProps={{ size: ButtonSize.medium }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className={classNames(
          isCompact
            ? "px-3 pt-1 pb-4"
            : "absolute inset-0 overflow-y-scroll pb-12 px-3 pt-4 top-14",
          meetingListClassName,
        )}
        ref={scrollContainerRef}
      >
        {/* Main loader on first page hit */}
        {loading && meetingsToDisplay.length === 0 ? (
          <Loading className="p-6" size={6}>
            Loading meetings...
          </Loading>
        ) : meetingsToDisplay.length === 0 ? (
          <div className="text-gray-500">No meetings.</div>
        ) : (
          <OverviewMeetingList
            ref={todayRef}
            meetings={meetingsToDisplay}
            loading={loading}
            participantFilter={participantFilter}
            meetingTypeFilter={meetingTypeFilter}
            meetingDisplayedRange={
              isCompact
                ? {
                    start: moment(selectedDay).startOf("day").format(),
                    end: moment(selectedDay).endOf("day").format(),
                  }
                : meetingDisplayedRange
            }
            onClickMeetingLink={onClickMeetingLink}
            canLoadMoreInFuture={hasMoreInFuture}
            canLoadMoreInPast={hasMoreInPast}
            loadMore={loadMore}
            onChangeLoadMore={setLoadMore}
            isShowingWeekends={true}
            fillerHeight={fillerHeight}
            compact={isCompact}
          />
        )}
      </div>
    </div>
  );
};

export default MeetingOverview;
